import React, { useState, useEffect, useCallback } from "react";
import { Inbox, CheckCircle2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { ccCard } from "./care/ui";

const API = process.env.REACT_APP_BACKEND_URL;

const CAT_STYLE = {
  Support: "bg-[#E6F2FA] border-[#B3E0F2] text-[#3D7E9E]",
  Store: "bg-[#FCE6E9] border-[#F8C8DC] text-[#B2607F]",
  General: "bg-[#FFF2D1] border-[#F5E3B3] text-[#A08244]",
};

// Support queue: contact-form inquiries (DB-backed, email notification sent on submit)
export default function SupportInquiries() {
  const [inquiries, setInquiries] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/master/inquiries`, { credentials: "include" });
      if (res.ok) setInquiries((await res.json()).inquiries);
    } catch (err) {
      console.error("Inquiries load failed", err);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const resolve = async (iid) => {
    try {
      const res = await fetch(`${API}/api/master/inquiries/${iid}`, { method: "PATCH", credentials: "include" });
      if (!res.ok) throw new Error();
      toast.success("Inquiry resolved.");
      load();
    } catch {
      toast.error("Could not resolve inquiry.");
    }
  };

  const open = (inquiries || []).filter((i) => i.status === "open");

  return (
    <section className={`${ccCard} p-6`} data-testid="support-inquiries">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
        <h3 className="text-base font-extrabold font-heading flex items-center gap-2.5">
          <span className="w-9 h-9 rounded-full bg-[#FCE6E9] flex items-center justify-center">
            <Inbox className="w-4 h-4 text-[#E5A0BC]" />
          </span>
          Support Queue
          {open.length > 0 && (
            <span className="px-2.5 py-0.5 bg-[#F8C8DC] text-[#374151] text-[10px] font-extrabold rounded-full" data-testid="inquiries-open-count">
              {open.length} open
            </span>
          )}
        </h3>
        <button onClick={load} data-testid="inquiries-refresh-btn" className="flex items-center gap-1.5 px-3.5 py-1.5 bg-[#B3E0F2] hover:bg-[#9ACCE0] text-[#374151] rounded-full text-[11px] font-bold transition-all">
          <RefreshCw className="w-3 h-3" /> Refresh
        </button>
      </div>
      {inquiries === null ? (
        <p className="text-xs text-[#9CA3AF] py-4 text-center font-medium">Loading inquiries…</p>
      ) : inquiries.length === 0 ? (
        <p className="text-xs text-[#9CA3AF] font-medium" data-testid="inquiries-empty">No inquiries yet — messages from the Contact page land here and are emailed to the admin queue.</p>
      ) : (
        <div className="space-y-2.5 max-h-72 overflow-y-auto">
          {inquiries.map((inq) => (
            <div key={inq.id} className="p-4 bg-[#FFF9F2] border border-[#FCE6E9] rounded-2xl" data-testid={`inquiry-row-${inq.id}`}>
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="flex items-center gap-2 min-w-0">
                  <span className={`px-2 py-0.5 border text-[9px] font-extrabold uppercase rounded-full ${CAT_STYLE[inq.category] || CAT_STYLE.General}`}>{inq.category}</span>
                  <p className="text-xs font-extrabold text-[#374151] truncate">{inq.subject}</p>
                </div>
                {inq.status === "open" ? (
                  <button onClick={() => resolve(inq.id)} data-testid={`inquiry-resolve-${inq.id}`} className="flex items-center gap-1 px-3 py-1.5 bg-[#E2F4E1] border border-[#C4E5C2] text-[#2F6031] rounded-full text-[10px] font-bold hover:bg-[#D2EBD1] transition-colors">
                    <CheckCircle2 className="w-3 h-3" /> Resolve
                  </button>
                ) : (
                  <span className="text-[10px] font-bold text-[#2F6031]" data-testid={`inquiry-resolved-${inq.id}`}>Resolved</span>
                )}
              </div>
              <p className="text-xs text-[#6B7280] mt-1.5">{inq.message}</p>
              <p className="text-[10px] text-[#9CA3AF] mt-1 font-medium">
                {inq.name} · {inq.email}
              </p>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
